import PropTypes from "prop-types";
import { useEffect } from "react";

import { window } from "@swa-ui/browser";
import { dateFormats, swaDate } from "@swa-ui/date";
import { getEnvironmentTier } from "@swa-ui/dotcom";
import { merge } from "@swa-ui/object";

import styles from "../HotelContainer/HotelContainer.module.scss";

export const ROCKET_WIDGET_ID = "cross-sell-root";
const ROCKET_SCRIPT_ID = "rocket-cross-sell-script";
const ROCKET_SCRIPT_PROD_URL = "https://widget.rocketmiles.com/cross-sell-script.js";
const ROCKET_SCRIPT_STAGING_URL = "https://widget-staging.rocketmiles.com/cross-sell-script.js";
const ISO_FORMAT = "YYYY-MM-DD";

/**
 * HotelTileScript will inject the script tag with Booking.com source URL and other parameters that is
 * required to render the iframe.
 */
export const HotelTileScript = (props) => {
  const {
    checkinDate,
    checkoutDate,
    dataContainer = {},
    formType,
    hotelIFrameData,
    hotelLocation,
    language,
    onReady,
    queryParameters,
  } = props;

  useEffect(() => {
    mountWidget();

    return () => {
      removeScriptAndWidget();
      delete window.getCrossSellSearchParameters;
      delete window.onCrossSellReady;
    };
  }, []);

  return null;

  function addDaysToDate(baseDate, daysToAdd) {
    return swaDate(baseDate)
      .set("date", swaDate(baseDate) + daysToAdd)
      .format(dateFormats?.iso || ISO_FORMAT);
  }

  function formatDate(dateString) {
    return dateString
      ? swaDate(dateString).format(dateFormats?.iso || ISO_FORMAT)
      : swaDate().format(dateFormats?.iso || ISO_FORMAT);
  }

  function getHotelScript() {
    const existingScript = window.document.getElementById(ROCKET_SCRIPT_ID);
    const isProd = getEnvironmentTier() === "prod";
    const baseUrl = isProd ? ROCKET_SCRIPT_PROD_URL : ROCKET_SCRIPT_STAGING_URL;
    let resultScript;

    if (existingScript) {
      resultScript = existingScript;
    } else {
      const newScript = window.document.createElement("script");

      newScript.id = ROCKET_SCRIPT_ID;
      newScript.type = "module";
      newScript.src = `${baseUrl}?cb=${new Date().getTime()}`;

      resultScript = newScript;
    }

    return resultScript;
  }

  function mountWidget() {
    const hotelFormTypeIFrameData = hotelIFrameData?.[formType] || {};
    const { queryParameters: bootstrapQueryParams = {} } = hotelFormTypeIFrameData;
    const config = merge(hotelFormTypeIFrameData, dataContainer);
    const finalCheckoutDate =
      !checkoutDate && checkinDate ? addDaysToDate(checkinDate, 2) : checkoutDate;
    const formattedCheckinDate = formatDate(checkinDate);
    const formattedCheckoutDate = finalCheckoutDate
      ? finalCheckoutDate.includes("-")
        ? finalCheckoutDate
        : formatDate(finalCheckoutDate)
      : undefined;
    const browserDocument = window.document;
    const hotelScript = getHotelScript();

    window.getCrossSellSearchParameters = () => ({
      region: hotelLocation,
      checkIn: formattedCheckinDate,
      checkOut: formattedCheckoutDate,
      guests: "1",
      currency: "USD",
      partner: "southwest_tr",
      language: language === "es" ? "es" : "en",
      layout: formType === "landing" ? "home_page_search" : "below_fold",
      queryParameters: { ...bootstrapQueryParams, ...queryParameters },
      ...(config?.attributes && { container: config.attributes["data-container"] }),
    });

    window.onCrossSellReady = () => {
      if (typeof onReady === "function") {
        onReady();
      }
    };

    window.onCrossSellUpdate = ({ params }) => {
      const { children } = params;
      const crossSellContainer = browserDocument.getElementById(ROCKET_WIDGET_ID);

      if (crossSellContainer && children !== undefined) {
        if (children > 0) {
          crossSellContainer.className = styles.crossSellRootExpanded;
        } else {
          crossSellContainer.className = styles.crossSellRoot;
        }
      }
    };

    if (!browserDocument.getElementById(ROCKET_SCRIPT_ID)) {
      browserDocument.head.appendChild(hotelScript);
    }
  }

  function removeScriptAndWidget() {
    const scriptElement = document.getElementById(ROCKET_SCRIPT_ID);
    const widgetElement = document.getElementById(ROCKET_WIDGET_ID);

    if (scriptElement) {
      scriptElement.remove();
    }

    if (widgetElement) {
      widgetElement.remove();
    }
  }
};

HotelTileScript.propTypes = {
  /** The default check in date */
  checkinDate: PropTypes.string,

  /** The default check out date */
  checkoutDate: PropTypes.string,

  /** This will contain attributes for data-container object which is the hotel title id. This could also
   * be any other object that is nested under attributes section
   */
  dataContainer: PropTypes.object,

  /**
   * This specifies which form are we loading hotelTile or hotelTileExpired or hotelTileMboxEnabled or
   * wideBookingDotComBanner. Based on this we will load the corresponding attributes from the config file
   * for the specified formType
   */
  formType: PropTypes.oneOf([
    "hotelTile",
    "hotelTileExpired",
    "hotelTileMboxEnabled",
    "landing",
    "wideBookingDotComBanner",
  ]),

  /** Pass bootstrapped app settings data in from the SPA here */
  hotelIFrameData: PropTypes.object,

  /** This is the hotel destination code being sent in case we know it already so the iframe can prepopulate the same */
  hotelLocation: PropTypes.string,

  /** This is for translation of the iframe component, we would send it as en_us or es for english and spanish */
  language: PropTypes.oneOf(["en_us", "es"]),

  /** Callback function to execute when the cross-sell is ready (optional) */
  onReady: PropTypes.func,

  /** Query parameters to be passed to the iframe and appended into the redirection URL. */
  queryParameters: PropTypes.object,
};
